import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../utils/api'
import StepIndicator from '../../components/common/StepIndicator'
import WelcomeStep from './activation/WelcomeStep'
import CredentialsStep from './activation/CredentialsStep'
import LGPDConsentStep from './activation/LGPDConsentStep'
import AdminCreationStep from './activation/AdminCreationStep'
import VerificationCodeStep from './activation/VerificationCodeStep'
import SuccessStep from './activation/SuccessStep'
import { toast } from '../../components/toast/CustomToast'
import logo from '../../assets/logo.png'

/**
 * Company Activation Wizard - 6 Steps
 * 1. Welcome - Introduction
 * 2. Credentials - Validate Company ID + API Token
 * 3. Admin Creation - Inform admin user email and password (data collected only)
 * 4. LGPD Consent - Accept LGPD terms (data collected only)
 * 5. Verification Code - Send activation code and verify (activation happens here)
 * 6. Success - Completion screen with redirect to login
 *
 * Important: Company activation and LGPD consent registration only happen in step 5
 * after the admin user is created and code is sent. This prevents partial activation
 * if user abandons the flow mid-process.
 */
const CompanyActivationPage = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [userId, setUserId] = useState(null)
  const [credentialsValidated, setCredentialsValidated] = useState(false)
  const [formData, setFormData] = useState({
    companyId: '',
    token: '',
    validatedCompany: null,
    lgpdAccepted: false,
    adminEmail: '',
    adminPassword: '',
    emailCode: ['', '', '', '', '', '']
  })

  const totalSteps = 6

  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') !== 'false'
    if (savedDarkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [])

  const handleNext = async () => {
    setError('')

    // Step 3: Admin Creation - Only collect data locally
    if (currentStep === 3) {
      // Validate that credentials were properly validated in step 2
      if (!credentialsValidated || !formData.companyId || !formData.token) {
        setError('Por favor, volte e valide suas credenciais corretamente.')
        return
      }
      setCurrentStep(4)
      return
    }

    // Step 4: LGPD Consent - Accept terms and REQUEST activation code (no user created yet)
    if (currentStep === 4) {
      setFormData(prev => ({ ...prev, lgpdAccepted: true }))
      setLoading(true)
      try {
        // Just request the activation code - NO USER CREATED YET
        await api.post('/company-activation/request-activation-code', {
          company_id: formData.companyId,
          token: formData.token,
          admin_email: formData.adminEmail,
          admin_password: formData.adminPassword
        })

        setLoading(false)
        setCurrentStep(5)
        toast.success('Código enviado! Verifique seu email para o código de ativação.')
      } catch (err) {
        const errorMsg = err.response?.data?.detail || 'Erro ao solicitar código de ativação. Tente novamente.'
        setError(errorMsg)
        setLoading(false)
      }
      return
    }

    // Step 5: Verification Code - Will be handled by VerificationCodeStep
    // (no action needed here - VerificationCodeStep handles validation and confirmation)
    if (currentStep === 5) {
      // Get user_id that was stored during code confirmation
      const storedUserId = localStorage.getItem('activation_user_id')
      if (storedUserId) {
        setUserId(storedUserId)
        localStorage.removeItem('activation_user_id')
      }
      setCurrentStep(6)
      return
    }

    // Step 6: Success - Complete activation
    if (currentStep === 6) {
      setLoading(true)
      try {
        await api.post('/company-activation/complete-activation', {
          user_id: userId
        })

        localStorage.setItem('company_id', formData.companyId)
        localStorage.setItem('company_name', formData.validatedCompany?.company_name || '')
        navigate('/login')
      } catch (err) {
        const errorMsg = err.response?.data?.detail || 'Erro ao completar ativação. Tente novamente.'
        setError(errorMsg)
        setLoading(false)
      }
      return
    }

    // Default: Move to next step
    setCurrentStep(currentStep + 1)
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
      setError('')
    }
  }

  return (
    <div className="h-screen overflow-hidden flex items-center justify-center p-4 relative" style={{ backgroundColor: '#0A1C28' }}>
      {/* Background blobs */}
      <div className="absolute top-0 -left-40 w-96 h-96 rounded-full filter blur-3xl opacity-10 animate-pulse" style={{ backgroundColor: '#25C6A4' }}></div>
      <div className="absolute bottom-0 -right-40 w-96 h-96 rounded-full filter blur-3xl opacity-10 animate-pulse" style={{ backgroundColor: '#25C6A4', animationDelay: '2s' }}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full filter blur-3xl opacity-5 animate-pulse" style={{ backgroundColor: '#25C6A4', animationDelay: '4s' }}></div>

      <div className="relative z-10 w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="logo-spin-border inline-flex items-center justify-center shrink-0" style={{}}>
              <img src={logo} alt="app" className="w-20 h-20 object-contain rounded-full" />
            </div>
          </div>
          <h1 className="text-3xl" style={{ fontFamily: 'Blare, sans-serif', color: '#FFFFFF' }}>
            Solid<span style={{ color: '#25C6A4' }}>.</span>Flow
          </h1>
          <p className="text-sm mt-1" style={{ fontFamily: "'Stolzl', sans-serif", color: 'rgba(255,255,255,0.6)' }}>
            Hub de Integrações
          </p>
        </div>

        {/* Step indicator */}
        <StepIndicator totalSteps={totalSteps} currentStep={currentStep} />

        {/* Form card */}
        <div
          className="rounded-2xl p-8 animate-fade-in backdrop-blur-2xl"
          style={{
            backgroundColor: 'rgba(10,28,40,0.85)',
            border: '1px solid rgba(37,198,164,0.2)',
            boxShadow: '0 0 40px rgba(37,198,164,0.08)'
          }}
        >
          {currentStep === 1 && <WelcomeStep onNext={handleNext} />}
          {currentStep === 2 && (
            <CredentialsStep
              formData={formData}
              setFormData={setFormData}
              setCredentialsValidated={setCredentialsValidated}
              onNext={handleNext}
              onBack={handleBack}
              loading={loading}
              error={error}
            />
          )}
          {currentStep === 3 && (
            <AdminCreationStep
              formData={formData}
              setFormData={setFormData}
              onNext={handleNext}
              onBack={handleBack}
              loading={loading}
              error={error}
            />
          )}
          {currentStep === 4 && (
            <LGPDConsentStep
              onNext={handleNext}
              onBack={handleBack}
              loading={loading}
              error={error}
            />
          )}
          {currentStep === 5 && (
            <VerificationCodeStep
              formData={formData}
              setFormData={setFormData}
              onNext={handleNext}
              onBack={handleBack}
              error={error}
              userId={userId}
              loading={loading}
            />
          )}
          {currentStep === 6 && (
            <SuccessStep formData={formData} onNext={handleNext} />
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm" style={{ color: 'rgba(255,255,255,0.35)' }}>
          <p>Etapa {currentStep} de {totalSteps} &nbsp;·&nbsp; Powered by <span style={{ fontFamily: 'Blare, sans-serif', color: '#FFFFFF' }}>Solid<span style={{ color: '#25C6A4' }}>.</span>Flow</span></p>
        </div>
      </div>
    </div>
  )
}

export default CompanyActivationPage
